import { FC, memo, useState } from 'react'

import { useForm, useStore } from '@tanstack/react-form'
import {
  ButtonGroup,
  Dialog,
  DialogFace,
  FF,
  LeaveConfirmation,
  RadioGroup,
  Toast,
  handleFormSubmissionError
} from 'ui/components'
import { Button } from 'ui/components/Buttons'
import { Col2x, FormRow } from 'ui/themes/globalStyles'
import { ChoiceOption } from 'ui/types'

import { KeySetupResult } from './KeySetupResult'
import { MetrcForm } from './forms'
import { BioTrackForm } from './forms/BioTrackForm'
import { DutchieForm } from './forms/DutchieForm'
import { postBioTrack, postDutchie, postMetrc } from './services'
import { BankKeySetupResponse, BioTrackFormPayload, DutchieFormPayload, MetrcFormPayload } from './types'

import * as SC from './styles'

type Props = {
  closeSetupKeyDialog: () => void
  reloadData: () => void
  licensesToAssign: ChoiceOption[]
}

export const AddConnectionDialog: FC<Props> = memo((props) => {
  const { closeSetupKeyDialog, reloadData, licensesToAssign } = props

  const [keySetupResult, setKeySetupResult] = useState<BankKeySetupResponse | undefined>(undefined)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const formSelector = useForm({
    defaultValues: {
      connectionType: 'Metrc'
    } as {
      connectionType: 'Metrc' | 'BioTrackTHC' | 'Dutchie POS'
    }
  })

  const successToast = () => {
    Toast.successSmall('Connection was added successfully')
  }

  const failedToast = () => {
    Toast.errorSmall('The key setup failed')
  }

  const selectedConnectionType = useStore(formSelector.store, (s) => s.values.connectionType)

  const metrcForm = useForm({
    defaultValues: new MetrcFormPayload(),
    onSubmit: ({ value }) => {
      setIsSubmitting(true)
      postMetrc(value)
        .then((data) => {
          setKeySetupResult(data)
          reloadData()
          if (data !== undefined && data.passedValidation !== undefined && data.passedValidation.length !== 0) {
            successToast()
          } else {
            failedToast()
          }
        })
        .catch((err: Error) => {
          handleFormSubmissionError<MetrcFormPayload>(err, metrcForm)
        })
        .finally(() => {
          setIsSubmitting(false)
        })
    }
  })

  const bioTrackForm = useForm({
    defaultValues: new BioTrackFormPayload(),
    onSubmit: ({ value }) => {
      setIsSubmitting(true)
      postBioTrack(value)
        .then(() => {
          successToast()
          reloadData()
          handleClose()
        })
        .catch((err: Error) => {
          handleFormSubmissionError<BioTrackFormPayload>(err, bioTrackForm)
        })
        .finally(() => {
          setIsSubmitting(false)
        })
    }
  })

  const dutchieForm = useForm({
    defaultValues: new DutchieFormPayload(),
    onSubmit: ({ value }) => {
      setIsSubmitting(true)
      postDutchie(value)
        .then(() => {
          successToast()
          reloadData()
          handleClose()
        })
        .catch((err: Error) => {
          handleFormSubmissionError<DutchieFormPayload>(err, dutchieForm)
        })
        .finally(() => {
          setIsSubmitting(false)
        })
    }
  })

  const handleSetup = async () => {
    switch (selectedConnectionType) {
      case 'Metrc':
        await metrcForm.handleSubmit()
        break
      case 'BioTrackTHC':
        await bioTrackForm.handleSubmit()
        break
      case 'Dutchie POS':
        await dutchieForm.handleSubmit()
        break
      default:
        throw new Error('Incompatible connection key type selection')
    }
  }

  const handleReset = () => {
    setKeySetupResult(undefined)
    metrcForm.reset()
  }

  const handleClose = () => {
    closeSetupKeyDialog()
  }

  const isMetrcDirty = useStore(metrcForm.store, (state) => state.isDirty)
  const isBioTrackDirty = useStore(bioTrackForm.store, (state) => state.isDirty)
  const isDutchieDirty = useStore(dutchieForm.store, (state) => state.isDirty)

  return (
    <LeaveConfirmation preventLeaving={isMetrcDirty || isBioTrackDirty || isDutchieDirty}>
      <Dialog
        id="add-connection-dialog"
        title="Add Connection"
        face={DialogFace.PRIMARY}
        isSubmitting={isSubmitting}
        onClose={handleClose}
        customFooterActions={
          <ButtonGroup margin="small">
            {keySetupResult !== undefined && selectedConnectionType === 'Metrc' ? (
              <Button face="primary" loading={isSubmitting} onClick={handleReset}>
                Setup Another Key
              </Button>
            ) : (
              <Button face="primary" loading={isSubmitting} onClick={handleSetup}>
                Setup
              </Button>
            )}
            <Button face="secondary" onClick={handleClose}>
              Cancel
            </Button>
          </ButtonGroup>
        }
      >
        <SC.Box>
          <FormRow>
            <Col2x>
              <formSelector.Field
                name="connectionType"
                children={(field) => (
                  <FF
                    label="Choose type of connection"
                    field={field}
                    input={(inputProps) => (
                      <RadioGroup
                        data={[
                          {
                            label: 'Metrc',
                            value: 'Metrc'
                          },
                          {
                            label: 'BioTrackTHC',
                            value: 'BioTrackTHC'
                          },
                          {
                            label: 'Dutchie POS',
                            value: 'Dutchie POS'
                          }
                        ]}
                        {...inputProps}
                      />
                    )}
                  />
                )}
              />
            </Col2x>
          </FormRow>

          {selectedConnectionType === 'Metrc' && (
            <>
              {keySetupResult !== undefined ? (
                <KeySetupResult keySetupResult={keySetupResult} />
              ) : (
                <MetrcForm form={metrcForm} />
              )}
            </>
          )}

          {selectedConnectionType === 'BioTrackTHC' && (
            <BioTrackForm form={bioTrackForm} licensesToAssign={licensesToAssign} />
          )}

          {selectedConnectionType === 'Dutchie POS' && (
            <DutchieForm form={dutchieForm} licensesToAssign={licensesToAssign} />
          )}
        </SC.Box>
      </Dialog>
    </LeaveConfirmation>
  )
})
